import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ValueChoiceGrid, type ValueChoiceItem } from "../../../../components/UIToolkit/Clickable/ValueChoiceGrid";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { ACCENT_BLUE, ACTION_NAVY, CONTROL_HEIGHT, FONT_SIZE, INK, LETTER_SPACING, NEUTRAL, STATUS } from "../../../../theme/tokens";
import { percentile, percentileRank } from "../../../../utils/statistics";

type Props = {
	open: boolean;
	onClose: () => void;
	onConfirm: (newCensus: number) => void;
	currentCensus: number;
	baselineCensus: number;
	censusDistribution: number[];
};

// Quick-select tiles are the TRUE percentiles of the census distribution (same type-7 algorithm the
// BE uses for the default target census), computed from `censusDistribution` — no approximation. The
// p70 tile therefore exactly equals `baselineCensus` (the BE default), so opening the dialog on the
// baseline highlights the tile that IS the real default.
const PERCENTILE_SHORTCUTS = [30, 40, 50, 60, 70, 80, 90];

const SECTION_LABEL_SX = {
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 700,
	letterSpacing: LETTER_SPACING.LABEL,
	textTransform: "uppercase" as const,
	color: INK.MUTED,
};

/** The committing footer action is ~52px and full width — see `SystemDialog`'s `fullWidthConfirm`. */
const APPLY_SX = {
	height: CONTROL_HEIGHT.XL,
	backgroundColor: ACTION_NAVY.MAIN,
	color: ACTION_NAVY.ON_ACTION,
	fontSize: FONT_SIZE.EMPHASIS,
	fontWeight: 700,
	"&:hover": { backgroundColor: ACTION_NAVY.DARK },
} as const;

/**
 * Pick the census the whole module predicts against: one of the distribution's own percentiles, or a
 * typed value.
 *
 * Built on `SystemDialog` (the canonical modal) with the percentile presets as a `ValueChoiceGrid`, so
 * the tiles' selected state, keyboard semantics and focus ring come from the design system rather than
 * from a hand-rolled `ButtonBase` grid painted in hex.
 */
const CensusModal: FC<Props> = ({ open, onClose, onConfirm, currentCensus, baselineCensus, censusDistribution }) => {
	const { t } = useTranslation();
	const [tempValue, setTempValue] = useState<number>(currentCensus);
	// Held as text so a mid-typing empty field doesn't snap back to 0 under the caret.
	const [draft, setDraft] = useState<string>(String(currentCensus));

	useEffect(() => {
		if (open) {
			setTempValue(currentCensus);
			setDraft(String(currentCensus));
		}
	}, [open, currentCensus]);

	const presets = useMemo<ValueChoiceItem[]>(
		() =>
			PERCENTILE_SHORTCUTS.map((p) => {
				const censusValue = censusDistribution.length > 0 ? Math.round(percentile(censusDistribution, p)) : baselineCensus;
				return {
					value: censusValue,
					label: String(censusValue),
					caption: t("hiring.census.percentile", { p }),
					ariaLabel: t("hiring.census.presetAria", { census: censusValue, p }),
					dataTestId: `hiring-census-chip-${p}`,
				};
			}),
		[censusDistribution, baselineCensus, t],
	);

	const livePercentile = censusDistribution.length > 0 ? percentileRank(censusDistribution, tempValue) : null;

	const commitDraft = (raw: string) => {
		const parsed = Number.parseInt(raw, 10);
		if (!Number.isFinite(parsed) || parsed < 0) {
			setDraft(String(tempValue));
			return;
		}
		setTempValue(parsed);
		setDraft(String(parsed));
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hiring.census.title")}
			hideCancel
			confirmButtonVariant="primary"
			confirm={t("hiring.census.apply")}
			confirmLeadingIcon={<CheckRoundedIcon sx={{ fontSize: 18 }} />}
			fullWidthConfirm
			confirmSx={APPLY_SX}
			onConfirm={() => onConfirm(tempValue)}
			confirmDisabled={tempValue < 0}
			// `xs` keeps the dialog near the design's width, which is what sizes the 4-up tiles correctly.
			maxWidth="xs"
			fullWidth
			dataTestId="hiring-census-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
					{/* The design's info panel, with the REAL values: the shipped target is the 70th percentile
					    over 60 days, so the copy names that rather than the mock's "median of the last month".
					    `Trans` keeps the sentence one translatable unit while bolding the figure inside it. */}
					<Box
						data-testid="hiring-census-info"
						sx={{
							display: "flex",
							alignItems: "flex-start",
							gap: 1.5,
							p: 2,
							borderRadius: 2,
							backgroundColor: STATUS.INFO.light,
							color: STATUS.INFO.dark,
						}}
					>
						<InfoOutlinedIcon sx={{ fontSize: 20, flexShrink: 0, mt: "1px" }} />
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.5 }}>
							<Trans i18nKey="hiring.census.body" values={{ p70: baselineCensus }} components={{ b: <Box component="span" sx={{ fontWeight: 700 }} /> }} />
						</Typography>
					</Box>

					<Box>
						<Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>{t("hiring.census.quickSelect")}</Typography>
						{/* No tile matches a typed custom value, and that "nothing selected" state is real —
						    `ValueChoiceGrid` takes `null` for it rather than forcing a false highlight. */}
						<ValueChoiceGrid
							items={presets}
							selected={presets.some((preset) => preset.value === tempValue) ? tempValue : null}
							onSelect={(value) => {
								setTempValue(Number(value));
								setDraft(String(value));
							}}
							columns={{ xs: 2, sm: 4 }}
							accentColor={ACCENT_BLUE.MAIN}
							dataTestId="hiring-census-presets"
						/>
					</Box>

					<Box>
						<Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>{t("hiring.census.customLabel")}</Typography>
						{/* A FILLED field, not an outlined one: the value is the thing being edited, so it reads
						    as a value on a surface rather than as one more form control. */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								px: 2,
								borderRadius: 2,
								minHeight: CONTROL_HEIGHT.LARGE,
								backgroundColor: NEUTRAL[150],
								border: "1px solid transparent",
								transition: "border-color 140ms ease",
								"&:focus-within": { borderColor: NEUTRAL[400], backgroundColor: NEUTRAL[50] },
							}}
						>
							<Box
								component="input"
								type="number"
								min={0}
								value={draft}
								aria-label={t("hiring.census.customLabel")}
								data-testid="hiring-census-input"
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraft(event.target.value)}
								onBlur={(event: React.FocusEvent<HTMLInputElement>) => commitDraft(event.target.value)}
								onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter") {
										commitDraft((event.target as HTMLInputElement).value);
									}
								}}
								sx={{
									flex: 1,
									minWidth: 0,
									border: "none",
									outline: "none",
									backgroundColor: "transparent",
									font: "inherit",
									fontSize: FONT_SIZE.TITLE_MD,
									fontWeight: 700,
									color: INK.PRIMARY,
									// The spinners fight a large bold value for the same few pixels.
									"&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
									MozAppearance: "textfield",
								}}
							/>
							<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: NEUTRAL[500], flexShrink: 0 }}>{t("hiring.census.unit")}</Typography>
						</Box>
						{livePercentile !== null && (
							<Typography data-testid="hiring-census-percentile-readout" sx={{ mt: 1, fontSize: FONT_SIZE.CAPTION, fontWeight: 600, color: NEUTRAL[500] }}>
								{t("hiring.census.percentileReadout", { p: livePercentile })}
							</Typography>
						)}
					</Box>
				</Box>
			}
		/>
	);
};

export default CensusModal;
